/**
 * レシピ詳細ページ
 * 動画の制御（SPはモーダル、PCはその場再生）
 * ピックアップスライダーの制御
 * LINEシェアボタンの制御
 */

import Splide from "@splidejs/splide";
import MicroModal from "micromodal";
import { breakpoint } from "../constant/breakpoint";

/**
 * 動画機能を初期化
 */
const initVideo = (): void => {
  const videoBtn = document.querySelector(
    "[data-recipe-video-btn]",
  ) as HTMLButtonElement;
  if (!videoBtn) return;

  const videoElement = document.querySelector(
    "[data-recipe-video-element]",
  ) as HTMLVideoElement;
  const modal = document.getElementById("recipe-modal") as HTMLElement;
  const modalVideoElement = modal?.querySelector("video") as HTMLVideoElement;

  if (!modal) return;

  // MicroModalを初期化
  MicroModal.init({
    disableScroll: false,
    awaitCloseAnimation: true,
  });

  /**
   * 画面幅を判定
   */
  const isMobile = (): boolean => {
    return window.matchMedia(
      `(max-width: ${Number.parseInt(breakpoint.md) - 1}px)`,
    ).matches;
  };

  /**
   * PC版の動画をその場で再生
   */
  const playVideoPc = (): void => {
    if (!videoElement) return;

    if (videoElement.paused) {
      videoElement.play();
      videoElement.muted = false;
    } else {
      videoElement.pause();
    }
  };

  /**
   * ボタンクリック時の処理
   */
  const handleVideoButtonClick = (): void => {
    if (isMobile()) {
      // SPはモーダルを開く
      MicroModal.show("recipe-modal", {
        onShow: () => {
          // スクロールを無効化
          document.body.classList.add("modal-open");
          // モーダル内の動画を再生
          if (modalVideoElement) {
            modalVideoElement.play();
          }
        },
        onClose: () => {
          // スクロールを有効化
          document.body.classList.remove("modal-open");
          // モーダル内の動画を停止
          if (modalVideoElement) {
            modalVideoElement.pause();
            modalVideoElement.currentTime = 0;
          }
        },
      });
    } else {
      // PCはその場で再生
      playVideoPc();
    }
  };

  // SP: モーダル開く前はコントロールバー非表示 / PC: コントロールバー表示＋自動再生
  if (videoElement) {
    if (isMobile()) {
      videoElement.removeAttribute("controls");
      // 動画の最初のフレームを表示（preload が無視されるモバイルに対して明示的にロードしシーク）
      videoElement.addEventListener(
        "loadedmetadata",
        () => {
          videoElement.currentTime = 0.001;
        },
        { once: true },
      );
      videoElement.load();
    } else {
      videoElement.setAttribute("controls", "");
      videoElement.play();
    }
  }

  // イベントリスナー登録
  videoBtn.addEventListener("click", handleVideoButtonClick);
};

/**
 * LINEシェアボタンを初期化
 */
const initShareButton = (): void => {
  const shareButton = document.querySelector(
    "[data-recipe-share-line]",
  ) as HTMLButtonElement;
  if (!shareButton) return;

  shareButton.addEventListener("click", () => {
    const url = encodeURIComponent(window.location.href);
    const lineUrl = `https://social-plugins.line.me/lineit/share?url=${url}`;
    window.open(lineUrl, "_blank", "width=600,height=600");
  });
};

/**
 * ピックアップスライダーを初期化
 */
const initPickupSlider = (): void => {
  const sliderElement = document.querySelector(
    "[data-recipe-pickup-slider]",
  ) as HTMLElement;
  if (!sliderElement) return;

  new Splide(sliderElement, {
    perPage: 4,
    perMove: 1,
    gap: "24px",
    padding: 0,
    arrows: true,
    pagination: false,
    breakpoints: {
      [Number.parseInt(breakpoint.md) - 1]: {
        perPage: 1,
        gap: "8px",
        padding: { left: "20px", right: "20px" },
        arrows: false,
        fixedWidth: "62%",
      },
    },
  }).mount();
};

/**
 * グリッド表示切替を初期化
 * グリッド表示のトグルとボタンのアイコン切替を制御
 */
const initGridToggle = (): void => {
  const gridToggleBtn = document.querySelector(
    "[data-recipe-grid-toggle]",
  ) as HTMLButtonElement;
  if (!gridToggleBtn) return;

  gridToggleBtn.addEventListener("click", () => {
    const makingSmalls = document.querySelectorAll(".making_smalls");

    for (const element of Array.from(makingSmalls)) {
      element.classList.toggle("is-compact");
    }

    // ボタンのアイコン切替
    gridToggleBtn.classList.toggle("is-active");
  });
};

// ======================================
// レシピ閲覧履歴：定数・型定義
// ======================================

const RECIPE_STORAGE_KEY = "hinatalife_viewed_recipes";
const LEGACY_RECIPE_STORAGE_KEY = "recipe_recent_articles";
const RECIPE_MAX_ITEMS = 30;

/**
 * レシピ閲覧履歴エントリ
 */
export interface ViewedRecipeEntry {
  handle: string;
  blogHandle: string;
  viewedAt: number;
  url: string;
  title: string;
  image: string;
  authorImg: string;
  authorName: string;
  authorPageUrl: string;
}

/**
 * 旧ストレージ（recipe_recent_articles）を削除する
 */
function removeLegacyRecipeStorage(): void {
  try {
    localStorage.removeItem(LEGACY_RECIPE_STORAGE_KEY);
  } catch {
    // エラーは無視
  }
}

/**
 * ローカルストレージからレシピ閲覧履歴を取得
 */
function getViewedRecipes(): ViewedRecipeEntry[] {
  try {
    const data = localStorage.getItem(RECIPE_STORAGE_KEY);
    if (!data) return [];

    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed)) return [];

    // url・title が存在しない旧フォーマットのエントリは除外する
    return parsed.filter((item): item is ViewedRecipeEntry => {
      return (
        typeof item === "object" &&
        item !== null &&
        typeof item.handle === "string" &&
        typeof item.viewedAt === "number" &&
        typeof item.url === "string" &&
        typeof item.title === "string"
      );
    });
  } catch {
    return [];
  }
}

/**
 * ローカルストレージにレシピ閲覧履歴を保存
 */
function saveViewedRecipes(recipes: ViewedRecipeEntry[]): void {
  try {
    localStorage.setItem(RECIPE_STORAGE_KEY, JSON.stringify(recipes));
  } catch {
    // ストレージ容量超過時などは無視
  }
}

/**
 * レシピ閲覧履歴のエントリリストを取得
 * @param excludeHandle - 除外するハンドル（現在の記事ページなど）
 */
export function getViewedRecipeEntries(
  excludeHandle?: string,
): ViewedRecipeEntry[] {
  return getViewedRecipes().filter(
    (r) => !excludeHandle || r.handle !== excludeHandle,
  );
}

/**
 * 閲覧履歴を保存（ハンドル + カード表示データ方式）
 */
const saveRecentArticle = (): void => {
  // 旧ストレージを削除（初回実行時のみ。以降は無害）
  removeLegacyRecipeStorage();

  const win = window as unknown as Record<string, unknown>;
  const articleHandle = win.__ARTICLE_HANDLE__ as string | undefined;
  const blogHandle = win.__BLOG_HANDLE__ as string | undefined;

  if (!articleHandle || !blogHandle) return;

  // DOM からカード表示データを取得
  const articleTitle = document
    .querySelector(".p-article-recipe__mv-title")
    ?.textContent?.trim();
  const thumbnailVideoElement = document.querySelector(
    ".p-article-recipe__thumbnail-video",
  ) as HTMLElement;
  const authorImg = document.querySelector(
    ".p-article-recipe__mv-author-img img",
  ) as HTMLImageElement;
  const authorName = document
    .querySelector(".p-article-recipe__mv-author-name")
    ?.textContent?.trim();
  const authorLink = document.querySelector(
    ".p-article-recipe__mv-author-link",
  ) as HTMLAnchorElement;

  if (!articleTitle) return;

  const newEntry: ViewedRecipeEntry = {
    handle: articleHandle,
    blogHandle,
    viewedAt: Date.now(),
    url: window.location.pathname,
    title: articleTitle,
    image: thumbnailVideoElement?.dataset.articleImage || "",
    authorImg: authorImg?.src || "",
    authorName: authorName || "",
    authorPageUrl: authorLink?.href || "",
  };

  const recipes = getViewedRecipes();

  // 重複チェック：同じハンドルがあれば削除
  const filtered = recipes.filter((r) => r.handle !== articleHandle);

  // 先頭に追加
  filtered.unshift(newEntry);

  // 最大件数を超えた場合は古いものを削除
  saveViewedRecipes(filtered.slice(0, RECIPE_MAX_ITEMS));
};

export const articleRecipe = (): void => {
  // 動画機能を初期化
  initVideo();
  // LINEシェアボタンを初期化
  initShareButton();
  // ピックアップスライダーを初期化
  initPickupSlider();
  // グリッド表示切替を初期化
  initGridToggle();
  // 閲覧履歴を保存
  saveRecentArticle();
};
